import { useState } from "react";
import type { Theme } from "./theme";

type Side = "agency" | "rarion";

export function OutsourceDiagram({ theme }: { theme: Theme }) {
  const [side, setSide] = useState<Side>("rarion");

  return (
    <div
      style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius * 1.5,
        padding: 28,
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: 3,
          background: theme.surface,
          borderRadius: theme.radius,
          marginBottom: 20,
          border: `1px solid ${theme.border}`,
        }}
      >
        {(["agency", "rarion"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setSide(k)}
            aria-pressed={side === k}
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "none",
              cursor: "pointer",
              borderRadius: theme.radius - 2,
              background: side === k ? theme.bg : "transparent",
              color: side === k ? theme.fg : theme.fgMute,
              boxShadow: side === k ? `0 1px 2px ${theme.border}` : "none",
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "inherit",
            }}
          >
            {k === "agency" ? "Outsourced" : "In-house with Rarion"}
          </button>
        ))}
      </div>

      <div style={{ position: "relative", height: 340 }}>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 8,
          }}
        >
          {["HM", "TA", "PEER", "EXEC"].map((r) => (
            <div
              key={r}
              style={{
                padding: "10px 8px",
                borderRadius: theme.radius,
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                fontSize: 11,
                fontFamily: theme.mono,
                color: theme.fgMute,
                textAlign: "center",
              }}
            >
              {r}
            </div>
          ))}
        </div>

        <div
          style={{
            position: "absolute",
            top: 130,
            left: "50%",
            transform: "translateX(-50%)",
            width: 200,
            padding: 20,
            textAlign: "center",
            borderRadius: theme.radius * 1.5,
            background: side === "rarion" ? theme.accent : theme.border,
            color: side === "rarion" ? theme.onAccent : theme.fg,
            transition: "all .35s ease",
            boxShadow: side === "rarion" ? `0 10px 30px ${theme.accentSoft}` : "none",
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              opacity: 0.7,
              marginBottom: 4,
            }}
          >
            Decision layer
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.4 }}>
            {side === "rarion" ? "Rarion" : "Agency black box"}
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          <div
            style={{
              padding: "12px",
              borderRadius: theme.radius,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
            }}
          >
            <div style={{ fontSize: 11, color: theme.fgMute, marginBottom: 4 }}>
              Hire confidence
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.fg }}>
              {side === "rarion" ? "92%" : "54%"}
            </div>
          </div>
          <div
            style={{
              padding: "12px",
              borderRadius: theme.radius,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
            }}
          >
            <div style={{ fontSize: 11, color: theme.fgMute, marginBottom: 4 }}>
              Retained learning
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.fg }}>
              {side === "rarion" ? "Yes" : "None"}
            </div>
          </div>
        </div>

        <svg
          aria-hidden="true"
          focusable="false"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          width="100%"
          height="100%"
        >
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1={`${(i + 0.5) * 25}%`}
              y1="40"
              x2="50%"
              y2="130"
              stroke={theme.accent}
              strokeWidth="1"
              strokeDasharray="2 4"
              opacity={side === "rarion" ? 0.7 : 0.25}
              style={{ transition: "opacity .35s" }}
            />
          ))}
          <line
            x1="50%"
            y1="210"
            x2="25%"
            y2="280"
            stroke={theme.accent}
            strokeWidth="1"
            strokeDasharray="2 4"
            opacity={side === "rarion" ? 0.7 : 0.25}
            style={{ transition: "opacity .35s" }}
          />
          <line
            x1="50%"
            y1="210"
            x2="75%"
            y2="280"
            stroke={theme.accent}
            strokeWidth="1"
            strokeDasharray="2 4"
            opacity={side === "rarion" ? 0.7 : 0.25}
            style={{ transition: "opacity .35s" }}
          />
        </svg>
      </div>
    </div>
  );
}
